import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useContent, C } from "@/hooks/useContent";
import { useSections, visible } from "@/hooks/useSections";
import { ArrowRight, Sparkles } from "lucide-react";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const DEFAULT_PORTRAIT = "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=900&q=80";
const DEFAULT_ACTION_SHOT = "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=900&q=80";

export default function AboutPage() {
  const content = useContent();
  const sections = useSections();
  const portrait = C(content, "about.portrait_url", DEFAULT_PORTRAIT);
  const action = C(content, "about.action_url", DEFAULT_ACTION_SHOT);
  useDocumentMeta({
    title: "About Marlowe · Northwestern Exterior Cleaning · North Seattle",
    description: "Meet Marlowe, the owner-operator behind Northwestern Exterior Cleaning. Licensed and insured pressure washing serving North Seattle homes and businesses.",
    path: "/about",
  });

  return (
    <div data-testid="about-page" className="max-w-6xl mx-auto px-6 lg:px-10 pt-14 pb-20">
      {/* Hero: portrait + intro */}
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7" data-testid="about-hero">
          <p className="overline text-muted-foreground">{C(content, "about.eyebrow", "About")}</p>
          <h1 className="font-display text-5xl lg:text-6xl font-semibold tracking-tighter mt-2 leading-[1.02]">
            {C(content, "about.heading", "Hi, I'm Marlowe.")}
          </h1>
          <p className="text-lg lg:text-xl text-muted-foreground mt-6 leading-relaxed max-w-xl">
            {C(content, "about.lede", "I started Northwestern Exterior Cleaning because I wanted to build something honest with my own hands, and because every concrete driveway in North Seattle deserves a better fate than the green-and-black drift of a wet winter.")}
          </p>
          <p className="font-display text-2xl text-primary italic mt-6">
            {C(content, "about.signature", "Marlowe")}
          </p>
        </div>
        <div className="lg:col-span-5" data-testid="about-portrait-wrap">
          <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-muted shadow-xl border border-border">
            <img
              src={portrait}
              alt="Marlowe at Northwestern Exterior Cleaning"
              data-testid="about-portrait-img"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.src = DEFAULT_PORTRAIT; }}
            />
          </div>
        </div>
      </div>

      {/* Stats */}
      {visible(sections, "about.stats") && (
      <div className="grid grid-cols-3 gap-4 mt-20" data-testid="about-stats">
        {[1, 2, 3].map((i) => (
          <div key={i} className="text-left" data-testid={`about-stat-${i}`}>
            <div className="font-display text-5xl lg:text-6xl font-semibold tracking-tighter text-primary">
              {C(content, `about.stat${i}_value`, "")}
            </div>
            <div className="overline text-muted-foreground mt-2">
              {C(content, `about.stat${i}_label`, "")}
            </div>
          </div>
        ))}
      </div>
      )}

      {/* My story */}
      {visible(sections, "about.body") && (
      <section className="mt-24 grid lg:grid-cols-12 gap-10" data-testid="about-story">
        <div className="lg:col-span-5">
          <p className="overline text-muted-foreground">{C(content, "about.story_eyebrow", "My story")}</p>
          <h2 className="font-display text-4xl lg:text-5xl font-semibold tracking-tighter mt-2 leading-tight">
            {C(content, "about.story_heading", "How a teenager with a pressure washer became your neighbor's go-to.")}
          </h2>
          <div className="mt-6 aspect-[4/3] rounded-2xl overflow-hidden bg-muted border border-border">
            <img
              src={action}
              alt="Marlowe at work"
              data-testid="about-action-img"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.src = DEFAULT_ACTION_SHOT; }}
            />
          </div>
        </div>
        <div className="lg:col-span-7 space-y-5 text-base lg:text-lg leading-relaxed text-foreground/85">
          <p>{C(content, "about.body1", "I picked up my first pressure washer when I was 16: a 3,000 PSI Honda from Craigslist. The very first job was my grandparents' driveway. I undercharged, took twice as long as I should have, and stripped a 6-inch streak of paint off the garage door. They tipped me anyway.")}</p>
          <p>{C(content, "about.body2", "That's the moment this business started. Not because I was good, but because I cared. I spent the next year learning the chemistry behind soft-washing wood, the right surface-cleaner RPM for paver joints, and why you never aim a 0° nozzle at anything you love.")}</p>
          <p>{C(content, "about.body3", "Today, Northwestern Exterior Cleaning serves North Seattle homeowners and small businesses with one focus: restore concrete, wood, brick, and pavers to honest-to-god new, at a price you can see before I show up.")}</p>
        </div>
      </section>
      )}

      {/* Personal blurb — "outside the truck" */}
      {visible(sections, "about.personal") && (
        <section className="mt-24" data-testid="about-personal">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            {C(content, "about.personal_image_url", "") && (
              <div className="lg:col-span-5 order-2 lg:order-1" data-testid="about-personal-image-wrap">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-muted border border-border">
                  <img
                    src={C(content, "about.personal_image_url", "")}
                    alt={C(content, "about.personal_heading", "Outside the truck")}
                    data-testid="about-personal-img"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                </div>
              </div>
            )}
            <div
              className={
                C(content, "about.personal_image_url", "")
                  ? "lg:col-span-7 order-1 lg:order-2"
                  : "lg:col-span-12 order-1"
              }
            >
              <p className="overline text-muted-foreground" data-testid="about-personal-eyebrow">
                {C(content, "about.personal_eyebrow", "Off the clock")}
              </p>
              <h2 className="font-display text-4xl lg:text-5xl font-semibold tracking-tighter mt-2 leading-tight" data-testid="about-personal-heading">
                {C(content, "about.personal_heading", "Who I am outside the truck")}
              </h2>
              <p className="mt-6 text-base lg:text-lg leading-relaxed text-foreground/85 whitespace-pre-line max-w-2xl" data-testid="about-personal-body">
                {C(content, "about.personal_body", "")}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      {visible(sections, "about.cta") && (
      <Card className="mt-24 p-10 lg:p-14 bg-primary text-primary-foreground border-none flex flex-col lg:flex-row lg:items-center justify-between gap-8" data-testid="about-cta">
        <div className="max-w-xl">
          <Sparkles className="w-5 h-5 text-white/60 mb-3" />
          <h2 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight">
            {C(content, "about.cta_heading", "Want to support a young local business?")}
          </h2>
          <p className="text-white/75 mt-3">{C(content, "about.cta_body", "Book a cleaning, refer a neighbor, or just send a kind email. All three help.")}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/booking">
            <Button data-testid="about-cta-book" size="lg" className="bg-white text-primary hover:bg-white/90 rounded-full h-12 px-7">
              Book a job <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/portfolio">
            <Button data-testid="about-cta-portfolio" variant="outline" size="lg" className="rounded-full h-12 px-7 bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white">
              See past work
            </Button>
          </Link>
        </div>
      </Card>
      )}
    </div>
  );
}
